import React, { useReducer, useCallback, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { MediaUploadBody } from '@adsk/acc-sitevision-web-lib';
import Button from '@adsk/alloy-react-button';
import { useLocale } from '@adsk/alloy-react-locale';
import Panel from '@adsk/alloy-react-panel';

import { useImperativeQuery } from './helpers/gql/hooks';
import {
  CREATE_PHOTO_ASSET,
  UPDATE_PHOTO,
  GET_PHOTOS,
} from './helpers/gql/queries';
import uploadsReducer, { actionTypes } from './helpers/reducers/uploads';
import {
  uploadFile,
  getBaseFilename,
  getDefaultFilename,
  fileIsVideo,
  FLAGS,
} from './helpers/util';
import { usePlangridFeatureFlag } from './PlangridLaunchDarklyProvider';

const StyledButton = styled(Button)`
  width: 100%;
`;

const PhotoCreate = ({
  projectId,
  onClose,
  open,
  floatingPanel,
  onCreateSuccess,
  sourceMetadata,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const [uploads, dispatch] = useReducer(uploadsReducer, []);
  const getProcessedPhotos = useImperativeQuery(GET_PHOTOS);
  const { value: canCopyLocation } = usePlangridFeatureFlag(
    FLAGS.PHOTO_LOCATION_COPY,
    false,
  );

  const uploadsAreComplete = useMemo(() => {
    return uploads.every((upload) => upload.status === 'complete');
  }, [uploads]);

  const handleClose = useCallback(() => {
    dispatch({ type: actionTypes.UPLOAD_CLEAR });
    onClose();
  }, [onClose]);

  const handleRemove = useCallback(
    (upload) =>
      dispatch({
        type: actionTypes.UPLOAD_REMOVE,
        payload: { uid: upload.uid },
      }),
    [],
  );

  const [createAsset] = useMutation(CREATE_PHOTO_ASSET);
  const [updatePhoto] = useMutation(UPDATE_PHOTO);

  const handleFileUpload = useCallback(
    (files) => {
      files.forEach((file) => {
        const title = getBaseFilename(file.name) || getDefaultFilename(file);
        const imageType = fileIsVideo(file) ? 'video' : 'normal';
        return createAsset({
          variables: {
            input: {
              projectUid: projectId,
              imageType,
              size: file.size,
            },
          },
        })
          .then(
            ({
              data: {
                ggCreatePhotoAsset: {
                  photoId,
                  photoUid,
                  method,
                  fields,
                  action,
                },
              },
            }) => {
              dispatch({
                type: actionTypes.UPLOAD_START,
                payload: { uid: photoUid, title, file },
              });
              const f = JSON.parse(fields);
              return uploadFile(file, { fields: f, action, method }).then(
                () => ({
                  photoId,
                  photoUid,
                }),
              );
            },
          )
          .then(({ photoId }) => {
            const input = {
              id: photoId,
              title,
              size: file.size,
              imageType,
              triggerProcessing: true,
            };
            if (
              canCopyLocation &&
              sourceMetadata &&
              sourceMetadata.locationId
            ) {
              input.locationId = sourceMetadata.locationId;
            }

            return updatePhoto({
              variables: {
                input,
                excludeAutotags: false,
              },
            });
          })
          .then(
            ({
              data: {
                ggUpdatePhoto: { photo },
              },
            }) => {
              dispatch({
                type: actionTypes.UPLOAD_COMPLETE,
                payload: { uid: photo.id, photo },
              });
            },
          );
      });
    },
    [createAsset, updatePhoto, projectId, sourceMetadata, canCopyLocation],
  );

  const handleCompleteClick = useCallback(() => {
    // try to get processed photos which include thumbnails and GPS data
    getProcessedPhotos({
      projectId,
      uids: uploads.map((upload) => upload.photo.id),
      limit: uploads.length,
    })
      .then(({ data }) => {
        const photoEntities = data?.ggProjectByUid?.photos?.edges?.map(
          (p) => p.node,
        );
        if (photoEntities.length > 0) {
          onCreateSuccess(photoEntities);
        }
      })
      .catch(() => {
        console.error(
          'There was an error fetching photos, falling back to the simple models',
        );
        onCreateSuccess(uploads.map((upload) => upload.photo));
      });
  }, [uploads, onCreateSuccess, getProcessedPhotos, projectId]);

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      renderHeader={() => (
        <Panel.Header
          onClose={handleClose}
          title={T.translate('UPLOAD_PHOTOS_AND_VIDEOS')}
        />
      )}
      renderFooter={({ style }) => (
        <div style={style}>
          <StyledButton
            size={Button.SIZES.LARGE}
            variant={Button.VARIANTS.PRIMARY}
            onClick={handleCompleteClick}
            disabled={uploads.length === 0 || !uploadsAreComplete}
          >
            {T.translate('UPLOAD')}
          </StyledButton>
        </div>
      )}
    >
      <Panel.Body>
        <MediaUploadBody
          uploads={uploads}
          uploadHandler={handleFileUpload}
          removeHandler={handleRemove}
        />
      </Panel.Body>
    </Panel>
  );
};

PhotoCreate.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  floatingPanel: PropTypes.bool,
  projectId: PropTypes.string,
  onCreateSuccess: PropTypes.func,
  sourceMetadata: PropTypes.object,
};

PhotoCreate.displayName = 'PhotoCreate';

export default PhotoCreate;
